/*
 * Copyright 2025 coze-dev Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import {
  ensureUcAccountInfoLoaded,
  getSessionAuthHeaders,
  resolveUcTenantIdFromStorage,
} from '@coze-foundation/account-adapter';

import { resolveOntologyRequestUrl } from './ontology-api-env';

/** 本体平台 API 请求鉴权头（Authorization + TenantId），与 tology-web getSessionAuthHeaders 一致 */
export function getOntologyAuthHeaders(): Record<string, string> {
  return getSessionAuthHeaders('/ontology-api');
}

/** 从 UC 会话同步读取 tenant_id（未就绪时返回 undefined） */
export function getOntologyTenantId(): string | undefined {
  return resolveUcTenantIdFromStorage();
}

/** 确保 UC 账号信息已加载后返回 tenant_id，供本体 API 请求使用 */
export async function resolveOntologyTenantId(): Promise<string> {
  const tenantId =
    (await ensureUcAccountInfoLoaded()) ?? resolveUcTenantIdFromStorage();
  if (!tenantId) {
    throw new Error('missing UC tenant_id for ontology API');
  }
  return tenantId;
}

/** 带 UC 鉴权头的 fetch，用于跨域直连本体平台 API */
export async function fetchOntologyApi(
  pathOrUrl: string,
  init: RequestInit = {},
): Promise<Response> {
  await ensureUcAccountInfoLoaded();
  const url = resolveOntologyRequestUrl(pathOrUrl);
  const fullUrl = url.toString();
  const headers = new Headers(init.headers);
  const auth = getOntologyAuthHeaders();
  for (const [key, value] of Object.entries(auth)) {
    if (!headers.has(key)) {
      headers.set(key, value);
    }
  }
  return fetch(fullUrl, { ...init, headers });
}
